import React, { useState, useEffect } from 'react';
import { Settings, AlertCircle, CheckCircle, ExternalLink, Copy, Eye, EyeOff } from 'lucide-react';
import apiService from '../services/apiService';
import { useI18n } from '../utils/i18n';
import { useToast } from './ToastProvider';
import './Setup.css';

const Setup = () => {
  const { t } = useI18n();
  const { pushToast } = useToast();
  const [setupData, setSetupData] = useState({
    clientId: '',
    clientSecret: '',
    tenantId: 'common',
    sessionSecret: '',
    allowedEmails: ''
  });
  const [, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [showSecret, setShowSecret] = useState(false);
  const [showSessionSecret, setShowSessionSecret] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const redirectUri = `${window.location.origin}/auth/callback`;

  useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      const response = await apiService.getSetupStatus();
      setStatus(response);
    } catch (error) {
      console.error('Error checking setup status:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setSetupData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔥 handleSubmit called');
    console.log('🔥 Current setupData:', setupData);
    setLoading(true);

    try {
      console.log('🔥 Sending request to /api/setup/configure');
      const response = await apiService.configureSetup({ ...setupData, redirectUri });
      console.log('🔥 Setup response:', response);
      
      if (response.success) {
        console.log('🔥 Setup successful, redirecting to /auth/login?setup=test');
        console.log('🔥 Current window.location.href:', window.location.href);
        
        // Direkt zur Authentifizierung mit Setup-Test weiterleiten
        window.location.href = '/auth/login?setup=test';
        
        console.log('🔥 After setting window.location.href');
      } else {
        console.error('🔥 Setup failed:', response);
        pushToast({ type: 'error', message: t('setup.failed', 'Setup fehlgeschlagen: {{details}}', { details: (response.error || t('common.unknownError', 'Unbekannter Fehler')) }) });
      }
    } catch (error) {
      console.error('🔥 Setup error:', error);
      console.error('🔥 Error response:', error.response?.data);
      pushToast({ type: 'error', message: t('setup.configError', 'Fehler bei der Konfiguration: {{details}}', { details: (error.response?.data?.error || error.message) }) });
    } finally {
      console.log('🔥 Setting loading to false');
      setLoading(false);
    }
  };

  const testConfiguration = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      // Kurze Verzögerung, um die Konfiguration zu initialisieren
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Weiterleitung zur Authentifizierung für den Test mit Setup-Parameter
      window.location.href = '/auth/login?setup=test';
    } catch (error) {
      console.error('Test error:', error);
      setTestResult({
        success: false,
        error: error.message
      });
      setTesting(false);
    }
  };

  const completeSetup = async () => {
    try {
      // Logout aus O365 um die Tenant-Session zu beenden
      await fetch('/auth/logout', { method: 'GET' });
      
      // Weiterleitung zur Startseite
      window.location.href = '/';
    } catch (error) {
      console.error('Error completing setup:', error);
      window.location.href = '/';
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const generateSessionSecret = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 64; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setSetupData(prev => ({ ...prev, sessionSecret: result }));
  };

  const renderStep1 = () => (
    <div className="setup-step">
      <h3>📋 {t('setup.step1.title', 'Schritt 1: Azure App Registration erstellen')}</h3>
      <div className="setup-content">
        <p>{t('setup.step1.intro', 'Erstellen Sie eine neue App-Registrierung in Azure:')}</p>
        <ol className="setup-instructions">
          <li>{t('setup.step1.goToPortal', 'Gehen Sie zum')} <a href="https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade/quickStartType~/null/sourceType/Microsoft_AAD_IAM" target="_blank" rel="noopener noreferrer">Microsoft Entra Admin Center <ExternalLink size={16} /></a></li>
          <li>{t('setup.clickOn', 'Klicken Sie auf')} <strong>New registration</strong></li>
          <li>{t('setup.step1.fillIn', 'Füllen Sie folgende Informationen aus:')}
            <ul>
                              <li><strong>Name:</strong> KayCee Ticket</li>
              <li><strong>Supported account types:</strong> Accounts in any organizational directory and personal Microsoft accounts</li>
              <li><strong>Redirect URI:</strong> Web - 
                <div className="copy-field">
                  <code>{redirectUri}</code>
                  <button 
                    type="button" 
                    onClick={() => copyToClipboard(redirectUri)}
                    className="copy-btn"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </li>
            </ul>
          </li>
          <li>{t('setup.clickOn', 'Klicken Sie auf')} <strong>Register</strong></li>
        </ol>
        <button 
          className="btn btn-primary"
          onClick={() => setStep(2)}
        >
          {t('setup.nextStep', 'Weiter zu Schritt {{n}}', { n: 2 })}
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="setup-step">
      <h3>🔑 {t('setup.step2.title', 'Schritt 2: Client Secret erstellen')}</h3>
      <div className="setup-content">
        <p>{t('setup.step2.intro', 'Erstellen Sie ein Client Secret für Ihre App:')}</p>
        <ol className="setup-instructions">
          <li>{t('setup.goTo', 'Gehen Sie zu')} <strong>Certificates & secrets</strong> → <strong>Client secrets</strong></li>
          <li>{t('setup.clickOn', 'Klicken Sie auf')} <strong>New client secret</strong></li>
          <li>{t('setup.step2.description', 'Geben Sie eine Beschreibung ein (z.B. "O365 Interface Secret")')}</li>
          <li>{t('setup.step2.expiry', 'Wählen Sie eine Gültigkeitsdauer (empfohlen: 24 Monate)')}</li>
          <li>{t('setup.clickOn', 'Klicken Sie auf')} <strong>Add</strong></li>
          <li><strong>{t('setup.important', 'Wichtig:')}</strong> {t('setup.step2.copyNow', 'Kopieren Sie das Secret sofort - es wird nur einmal angezeigt!')}</li>
        </ol>
        <button 
          className="btn btn-secondary"
          onClick={() => setStep(1)}
        >
          {t('common.back', 'Zurück')}
        </button>
        <button 
          className="btn btn-primary"
          onClick={() => setStep(3)}
          style={{ marginLeft: '10px' }}
        >
          {t('setup.nextStep', 'Weiter zu Schritt {{n}}', { n: 3 })}
        </button>
      </div>
    </div>
  );

  const renderStep3Permissions = () => (
    <div className="setup-step">
      <h3>🛡️ {t('setup.step3.title', 'Schritt 3: API-Berechtigungen konfigurieren')}</h3>
      <div className="setup-content">
        <p>{t('setup.step3.intro', 'Fügen Sie die erforderlichen API-Berechtigungen hinzu:')}</p>
        <ol className="setup-instructions">
          <li>{t('setup.goTo', 'Gehen Sie zu')} <strong>API permissions</strong></li>
          <li>{t('setup.clickOn', 'Klicken Sie auf')} <strong>Add a permission</strong></li>
          <li>{t('setup.choose', 'Wählen Sie')} <strong>Microsoft Graph</strong> → <strong>Delegated permissions</strong></li>
          <li>{t('setup.step3.addFollowing', 'Fügen Sie folgende Berechtigungen hinzu:')}
            <ul>
              <li><strong>User.Read</strong> - {t('setup.perm.userRead', 'Benutzerprofil lesen')}</li>
              <li><strong>Mail.Read</strong> - {t('setup.perm.mailRead', 'E-Mails lesen')}</li>
              <li><strong>Mail.ReadWrite</strong> - <span style={{color: 'var(--color-danger)', fontWeight: 'bold'}}>{t('setup.new', 'NEU:')}</span> {t('setup.perm.mailReadWrite', 'E-Mails bearbeiten (für Gelesen/Ungelesen Status)')}</li>
              <li><strong>Mail.Send</strong> - <span style={{color: 'var(--color-danger)', fontWeight: 'bold'}}>{t('setup.new', 'NEU:')}</span> {t('setup.perm.mailSend', 'E-Mails versenden')}</li>
              <li><strong>Calendars.Read</strong> - {t('setup.perm.calRead', 'Kalender lesen')}</li>
              <li><strong>Calendars.ReadWrite</strong> - {t('setup.perm.calReadWrite', 'Kalendereinträge erstellen/bearbeiten (eigener Kalender)')}</li>
              <li><strong>Calendars.ReadWrite.Shared</strong> - <span style={{color: 'var(--color-danger)', fontWeight: 'bold'}}>{t('setup.new', 'NEU:')}</span> {t('setup.perm.calShared', 'Kalendereinträge in geteilten/delegierten Kalendern')}</li>
            </ul>
          </li>
          <li>{t('setup.clickOn', 'Klicken Sie auf')} <strong>Add permissions</strong></li>
          <li><strong>{t('setup.important', 'Wichtig:')}</strong> {t('setup.clickOn', 'Klicken Sie auf')} <strong>Grant admin consent</strong> {t('setup.step3.forAllPermissions', 'für alle Berechtigungen')}</li>
        </ol>
        <div className="warning-box">
          <AlertCircle size={20} />
          <strong>{t('setup.important', 'Wichtig:')}</strong> {t('setup.step3.adminConsentRequired', 'Die Administratoreinwilligung ist erforderlich für den ordnungsgemäßen Betrieb!')}
        </div>
        <div className="info-box" style={{ marginTop: '15px', backgroundColor: 'var(--color-info-bg-light)', border: '1px solid var(--color-primary)' }}>
          <AlertCircle size={20} style={{ color: 'var(--color-primary)' }} />
          <div>
            <strong>{t('setup.step3.newFeatures', 'Neue Features verfügbar:')}</strong>
            <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
              <li>✅ {t('setup.step3.featureMarkRead', 'E-Mails als gelesen/ungelesen markieren')}</li>
              <li>✅ {t('setup.step3.featureSend', 'E-Mails direkt aus der Anwendung senden')}</li>
            </ul>
          </div>
        </div>
        <button 
          className="btn btn-secondary"
          onClick={() => setStep(2)}
        >
          {t('common.back', 'Zurück')}
        </button>
        <button 
          className="btn btn-primary"
          onClick={() => setStep(4)}
          style={{ marginLeft: '10px' }}
        >
          {t('setup.nextStep', 'Weiter zu Schritt {{n}}', { n: 4 })}
        </button>
      </div>
    </div>
  );

  const renderStep4Config = () => (
    <div className="setup-step">
      <h3>⚙️ {t('setup.step4.title', 'Schritt 4: Konfiguration eingeben')}</h3>
      <div className="setup-content">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>
              <strong>Application (Client) ID:</strong>
              <small>{t('setup.step4.clientIdHelp', 'Finden Sie diese auf der Overview-Seite Ihrer App')}</small>
            </label>
            <input
              type="text"
              value={setupData.clientId}
              onChange={(e) => handleInputChange('clientId', e.target.value)}
              placeholder={t('setup.step4.clientIdPlaceholder', 'z.B. 12345678-1234-1234-1234-123456789012')}
              required
            />
          </div>

          <div className="form-group">
            <label>
              <strong>Client Secret:</strong>
              <small>{t('setup.step4.secretHelp', 'Das Secret, das Sie gerade erstellt haben')}</small>
            </label>
            <div className="password-field">
              <input
                type={showSecret ? 'text' : 'password'}
                value={setupData.clientSecret}
                onChange={(e) => handleInputChange('clientSecret', e.target.value)}
                placeholder={t('setup.step4.secretPlaceholder', 'Ihr Client Secret')}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowSecret(!showSecret)}
              >
                {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>
              <strong>Tenant ID ({t('common.optional', 'Optional')}):</strong>
              <small>{t('setup.step4.tenantHelp', 'Leer lassen für Multi-Tenant oder spezifische Tenant-ID eingeben')}</small>
            </label>
                        <input
              type="text"
              value={setupData.tenantId}
              onChange={(e) => handleInputChange('tenantId', e.target.value)}
              placeholder={t('setup.step4.tenantPlaceholder', 'common (für Multi-Tenant)')}
            />
          </div>

          <div className="form-group">
            <label>
              <strong>Session Secret:</strong>
              <small>{t('setup.step4.sessionHelp', 'Automatisch generiert oder eigenes eingeben')}</small>
            </label>
            <div className="session-secret-field">
              <input
                type={showSessionSecret ? 'text' : 'password'}
                value={setupData.sessionSecret}
                onChange={(e) => handleInputChange('sessionSecret', e.target.value)}
                placeholder={t('setup.step4.sessionPlaceholder', 'Automatisch generiert')}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowSessionSecret(!showSessionSecret)}
              >
                {showSessionSecret ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              <button
                type="button"
                className="generate-btn"
                onClick={generateSessionSecret}
              >
                {t('setup.step4.generate', 'Generieren')}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>
              <strong>{t('setup.step4.allowedEmails', 'Erlaubte E-Mail-Adressen')} ({t('common.optional', 'Optional')}):</strong>
              <small>{t('setup.step4.allowedEmailsHelp', 'Beschränken Sie den Zugang auf bestimmte E-Mail-Adressen oder Domains. Leer lassen für alle O365-Benutzer.')}</small>
            </label>
            <textarea
              value={setupData.allowedEmails}
              onChange={(e) => handleInputChange('allowedEmails', e.target.value)}
              placeholder={t('setup.step4.allowedEmailsPlaceholder', 'Beispiele:\nuser1@example.com, user2@example.com\n*@example.com\nadmin@company.de, *@company.de')}
              rows="3"
              style={{ resize: 'vertical' }}
            />
            <div className="form-help">
              <strong>{t('setup.step4.formatting', 'Formatierung:')}</strong>
              <ul>
                <li><code>user@example.com</code> - {t('setup.step4.fmtExact', 'Exakte E-Mail-Adresse')}</li>
                <li><code>*@example.com</code> - {t('setup.step4.fmtDomain', 'Alle Adressen der Domain')}</li>
                <li>{t('setup.step4.fmtComma', 'Mehrere Adressen mit Komma trennen')}</li>
              </ul>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="button"
              className="btn btn-secondary"
              onClick={() => setStep(3)}
            >
              {t('common.back', 'Zurück')}
            </button>
            <button 
              type="submit"
              className="btn btn-primary"
              disabled={loading || !setupData.clientId || !setupData.clientSecret}
            >
              {loading ? t('setup.step4.saving', 'Konfiguriere...') : t('setup.step4.save', 'Konfiguration speichern')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="setup-step">
      <h3>✅ {t('setup.step5.title', 'Setup abgeschlossen!')}</h3>
      <div className="setup-content">
        <div className="success-message">
          <CheckCircle size={48} style={{ color: 'var(--color-success-alt)', marginBottom: '20px' }} />
          <p>{t('setup.step5.saved', 'Die Konfiguration wurde erfolgreich gespeichert!')}</p>
          
          {testing && (
            <div className="testing-section">
              <div className="loading-spinner"></div>
              <p>{t('setup.step5.testing', 'Teste die Konfiguration...')}</p>
            </div>
          )}

          {testResult && (
            <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
              {testResult.success ? (
                <div>
                  <h4>✅ {t('setup.step5.testOk', 'Test erfolgreich!')}</h4>
                  <p>{t('setup.step5.worksFine', 'Die Konfiguration funktioniert korrekt.')}</p>
                  <button 
                    className="btn btn-success"
                    onClick={completeSetup}
                  >
                    {t('setup.step5.finish', 'Setup beenden')}
                  </button>
                </div>
              ) : (
                <div>
                  <h4>❌ {t('setup.step5.testFailed', 'Test fehlgeschlagen')}</h4>
                  <p>{t('common.errorPrefix', 'Fehler:')} {testResult.error}</p>
                  <button 
                    className="btn btn-warning"
                    onClick={testConfiguration}
                  >
                    {t('setup.step5.retest', 'Erneut testen')}
                  </button>
                </div>
              )}
            </div>
          )}

          {!testing && !testResult && (
            <div>
              <p>{t('setup.step5.autoTest', 'Die Konfiguration wird automatisch getestet...')}</p>
              <button 
                className="btn btn-primary"
                onClick={testConfiguration}
              >
                {t('setup.step5.manualTest', 'Manuell testen')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="container">
      <div className="setup-container">
        <div className="setup-header">
          <Settings size={32} style={{ color: 'var(--color-primary)' }} />
                      <h1>KayCee Ticket – Setup</h1>
          <p>{t('setup.subtitle', 'Konfigurieren Sie Ihre Microsoft 365 Integration')}</p>
        </div>

        <div className="setup-steps">
          <div className="step-indicator">
            <div className={`step ${step >= 1 ? 'active' : ''}`}>1</div>
            <div className={`step ${step >= 2 ? 'active' : ''}`}>2</div>
            <div className={`step ${step >= 3 ? 'active' : ''}`}>3</div>
            <div className={`step ${step >= 4 ? 'active' : ''}`}>4</div>
            <div className={`step ${step >= 5 ? 'active' : ''}`}>5</div>
          </div>

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3Permissions()}
          {step === 4 && renderStep4Config()}
          {step === 5 && renderStep4()}
        </div>

        <div className="setup-info">
          <div className="info-box">
            <AlertCircle size={20} style={{ color: 'var(--color-warning-text)' }} />
            <div>
              <strong>{t('setup.info.permissions', 'Benötigte Berechtigungen:')}</strong>
              <ul>
                <li>User.Read - {t('setup.info.userRead', 'Zum Lesen der Benutzerinformationen')}</li>
                <li>Mail.Read - {t('setup.info.mailRead', 'Zum Lesen der E-Mails')}</li>
                <li>Mail.ReadWrite - <strong>{t('setup.new', 'NEU:')}</strong> {t('setup.info.mailReadWrite', 'Zum Bearbeiten von E-Mails (Gelesen/Ungelesen)')}</li>
                <li>Mail.Send - <strong>{t('setup.new', 'NEU:')}</strong> {t('setup.info.mailSend', 'Zum Versenden von E-Mails')}</li>
                <li>Calendars.Read - {t('setup.info.calRead', 'Zum Lesen der Kalender-Termine')}</li>
                <li>Calendars.ReadWrite - {t('setup.info.calReadWrite', 'Zum Erstellen/Bearbeiten von Kalendereinträgen (eigener Kalender)')}</li>
                <li>Calendars.ReadWrite.Shared - <strong>{t('setup.new', 'NEU:')}</strong> {t('setup.info.calShared', 'Zum Erstellen von Kalendereinträgen in geteilten Kalendern')}</li>
              </ul>
            </div>
          </div>

          <div className="info-box">
            <AlertCircle size={20} style={{ color: 'var(--color-info-alt)' }} />
            <div>
              <strong>Redirect URI:</strong><br />
              <code>{redirectUri}</code>
            </div>
          </div>

          <div className="info-box">
            <AlertCircle size={20} style={{ color: 'var(--color-success-alt)' }} />
            <div>
              <strong>{t('setup.info.access', 'Zugriffskontrolle:')}</strong>
              <ul>
                <li>✅ {t('setup.info.accessRestrict', 'Beschränkung auf bestimmte E-Mail-Adressen möglich')}</li>
                <li>✅ {t('setup.info.accessWildcard', 'Wildcard-Unterstützung für ganze Domains (*@domain.com)')}</li>
                <li>✅ {t('setup.info.accessFlexible', 'Flexible Konfiguration über Setup-Prozess')}</li>
                <li>💡 {t('setup.info.accessEmpty', 'Leer lassen für alle O365-Benutzer')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Setup; 